import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty, isFunction } from 'lodash'
import PropTypes from 'prop-types'

import Wizard from '@ublique/components/Wizard'
import Loading from '@ublique/components/Loading'
import Creation from 'components/Wizard/components/Creation'
import FormStep from 'components/Wizard/components/FormStep'
import ErrorConfig from 'components/Wizard/components/ErrorConfig'

import { wizardStore } from 'state'
import { updateState } from 'pages/Scenario/utils/state'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import useDateTimeFormat from 'utils/hooks/useDateTimeFormat'
import { initialState as wizardInitialState } from 'components/Wizard/state'
import {
  generateCreationObject,
  processedScheduledData
} from 'components/Wizard/helpers/container'

import {
  STEPS_STAGE,
  CREATION_STAGE,
  SCHEDULED_STAGE
} from 'components/Wizard/constants'
import CronScheduledStage from 'components/CronScheduledStage'
import '../index.scss'

const selectScenarioWizardModel = (state) => state.scenarioModelConfig.wizard
// Context for custom wizard components coming from the product

const ScenarioContainer = ({ open, close, data }) => {
  const {
    clones,
    scenarios,
    onSave,
    headingLabel,
    defaultStage = CREATION_STAGE,
    defaultScheduledData,
    isScheduledEditing,
    jobTypes
  } = data
  const scenarioWizardModel = useStoreSelector(
    'appConfig',
    selectScenarioWizardModel
  )

  const [stage, setStage] = useState(defaultStage)
  const [cloneInfo, setCloneInfo] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [creationValues, setCreationValues] = useState(null)
  const formatOptions = useDateTimeFormat()

  const { t } = useTranslation(['Wizard', 'WizardPlatform'])
  const { sub: username } = useStoreSelector('user')
  const {
    forceClone = false,
    scenarioForcedToClone = {},
    status,
    inputElements,
    formData: flattenValues,
    isScheduledMode,
    cronScheduled,
    payloadMapper
  } = useStoreSelector('wizard')

  const {
    config,
    enumScenarioType,
    cloning = true,
    scenarioDocumentType = {}
  } = scenarioWizardModel

  const changeOpen = (value) => {
    if (!value) {
      setStage(CREATION_STAGE)
      setCreationValues(null)
      updateState(wizardStore, wizardInitialState)
    }
    close()
  }

  const onCreationSubmit = ({
    name,
    type,
    clone: { document, scenario },
    notes,
    masterdataInstance,
    instanceId,
    creationMode
  }) => {
    const newState = {
      instanceId,
      scenarioType: type
    }

    if (!isEmpty(document) && !isEmpty(scenario)) {
      newState.clone = document

      setCloneInfo({
        scenarioToCopyCode: document.code,
        scenarioToCopyTitle: document.title,
        masterdata: scenario.masterdata,
        masterdataName: scenario.masterdataName,
        parent: scenario.id,
        parentName: scenario.name
      })
    } else {
      const { id, title } = masterdataInstance

      setCloneInfo({
        masterdata: id,
        masterdataName: title,
        parent: id,
        parentName: title
      })
    }

    updateState(wizardStore, newState)

    setStage(STEPS_STAGE)
    setCreationValues({ name, creationMode, type, notes, instanceId })
  }

  const saveScheduledFormData = async (scheduledData) => {
    if (isScheduledEditing) {
      const body = processedScheduledData({
        scheduledData,
        scenarioResult: defaultScheduledData.payload,
        payloadMapper: defaultScheduledData.payloadMapper,
        isScheduledEditing: true
      })
      try {
        await onSave(body)
        close()
      } catch (error) {
        close()
        console.error(error)
      }
    } else {
      updateState(wizardStore, {
        cronScheduled: scheduledData,
        isScheduledMode: true
      })

      setStage(CREATION_STAGE)
    }
  }

  const onFormSave = async (formData) => {
    if (!isFunction(onSave)) {
      close()
      return
    }

    setIsLoading(true)

    const { type } = creationValues

    const {
      summary,
      source: { documentFilters }
    } = config[type]

    const forcedCloneInfo = {
      cloned: true,
      scenarioToCopyCode: document?.code,
      scenarioToCopyTitle: scenarioForcedToClone?.value,
      masterdata: scenarioForcedToClone?.masterdata,
      masterdataName: scenarioForcedToClone?.masterdataName,
      parent: scenarioForcedToClone?.id,
      parentName: scenarioForcedToClone?.value
    }

    const scenarioResult = generateCreationObject({
      t,
      status,
      username,
      formData,
      cloneInfo: forceClone ? forcedCloneInfo : cloneInfo,
      flattenValues,
      inputElements,
      creationValues,
      documentFilters,
      formatOptions,
      scenarioSummary: summary,
      forceClone
    })

    const body = isScheduledMode
      ? processedScheduledData({
          scheduledData: cronScheduled,
          scenarioResult,
          payloadMapper
        })
      : scenarioResult

    try {
      await onSave(body)
      setIsLoading(false)
      close()
    } catch (error) {
      console.error(error)
    }
  }

  const wizardTitle = useMemo(() => {
    if (stage === SCHEDULED_STAGE) {
      return isScheduledEditing
        ? t('WizardPlatform:EditingScheduledHeading')
        : t('WizardPlatform:ScheduledHeading')
    } else {
      return t(headingLabel)
    }
  }, [stage, isScheduledEditing, t])

  if (!open) {
    return null
  }

  const renderFormStep = () => {
    const formConfig = config[creationValues.type]

    if (!formConfig) {
      return <ErrorConfig onClose={() => close()} />
    }

    return (
      <FormStep
        onSave={onFormSave}
        changeOpen={changeOpen}
        creationData={creationValues}
        config={formConfig}
      />
    )
  }

  return (
    <div
      id='wizard-container'
      className={
        stage === CREATION_STAGE || stage === SCHEDULED_STAGE
          ? 'creation-step'
          : 'form-step'
      }
    >
      <Wizard
        open={open}
        onClose={close}
        onRequestSubmit={onSave}
        headingLabel={wizardTitle}
      >
        {isLoading && <Loading />}

        {stage === SCHEDULED_STAGE && (
          <CronScheduledStage
            changeOpen={changeOpen}
            saveScheduledFormData={saveScheduledFormData}
            defaultScheduledData={defaultScheduledData}
            isScheduledEditing={isScheduledEditing}
            jobTypes={jobTypes}
          />
        )}

        {stage === CREATION_STAGE && (
          <Creation
            clones={clones}
            scenarios={scenarios}
            cloning={cloning}
            changeOpen={changeOpen}
            onSubmit={onCreationSubmit}
            enumScenarioType={enumScenarioType}
            scenarioDocumentType={scenarioDocumentType}
          />
        )}
        {stage === STEPS_STAGE && creationValues && renderFormStep()}
      </Wizard>
    </div>
  )
}

ScenarioContainer.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  onSave: PropTypes.func,
  clones: PropTypes.array,
  extConfig: PropTypes.object,
  headingLabel: PropTypes.string
}

export default React.memo(ScenarioContainer)
